import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { VERDICT_META, type Analysis } from "../../shared/types";
import { ExtractionTag } from "./ExtractionPanel";

interface Line {
  key: number;
  a: Analysis;
  resolved: boolean;
}

const TICK_MS = 1000;
const RESOLVE_MS = 420;
const KEEP = 7;

/** Auto-playing stream: prompts arrive, get screened, and resolve to a verdict. */
export function LiveClassifier({ items }: { items: Analysis[] }) {
  const [lines, setLines] = useState<Line[]>([]);
  const [count, setCount] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!items.length) return;
    // Shuffle once so the stream isn't just the newest-first order.
    const pool = [...items].sort(() => Math.random() - 0.5);
    let i = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Prefill so the window reads as a running log immediately.
    const prefill = pool.slice(0, KEEP - 1).map((a) => ({ key: seq.current++, a, resolved: true }));
    i = prefill.length;
    setLines(prefill);
    setCount(prefill.length);

    const tick = () => {
      const a = pool[i % pool.length]!;
      i++;
      const key = seq.current++;
      setLines((prev) => [...prev.slice(-(KEEP - 1)), { key, a, resolved: false }]);
      timers.push(
        setTimeout(() => {
          setLines((prev) => prev.map((l) => (l.key === key ? { ...l, resolved: true } : l)));
          setCount((c) => c + 1);
        }, RESOLVE_MS),
      );
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(id);
      timers.forEach(clearTimeout);
    };
  }, [items]);

  return (
    <div className="win">
      <div className="win-bar">
        <span>fenz-classify --stream</span>
        <span className="hatch" />
        <span className="tabular-nums">{String(count).padStart(4, "0")}</span>
      </div>

      <div className="flex h-[188px] flex-col justify-end gap-[3px] overflow-hidden p-2">
        <AnimatePresence initial={false}>
          {lines.map((l) => (
            <StreamRow key={l.key} line={l} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StreamRow({ line }: { line: Line }) {
  const { a, resolved } = line;
  const m = VERDICT_META[a.verdict];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="mono relative flex shrink-0 items-center gap-2 overflow-hidden whitespace-nowrap text-[11px] leading-none"
    >
      {resolved ? (
        <>
          <span
            className="w-[86px] shrink-0 border-[1.5px] border-ink px-1 py-[3px] text-center text-[9.5px] font-bold tracking-[0.08em]"
            style={{
              background: m.accent,
              color: a.verdict === "suspicious" ? "var(--ink)" : "var(--paper)",
            }}
          >
            {m.label.toUpperCase()}
          </span>
          <span className="w-[38px] shrink-0 tabular-nums font-bold" style={{ color: m.accent }}>
            {a.topHazardScore.toFixed(2)}
          </span>
          <span className="hidden w-[120px] shrink-0 truncate opacity-55 sm:inline">{a.topHazard}</span>
          <span className="hidden w-[46px] shrink-0 tabular-nums opacity-45 sm:inline">{a.latencyMs}ms</span>
          {a.extraction && a.extraction.level !== "clear" && (
            <ExtractionTag level={a.extraction.level} />
          )}
          <span className="truncate opacity-85">{a.text}</span>
        </>
      ) : (
        <>
          <span className="relative w-[86px] shrink-0 overflow-hidden border-[1.5px] border-ink bg-grey px-1 py-[3px] text-center text-[9.5px] tracking-[0.08em]">
            <span className="sweep" />
            <span className="relative">·····</span>
          </span>
          <span className="truncate opacity-45">{a.text}</span>
        </>
      )}
    </motion.div>
  );
}
